import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useReactToPrint } from "react-to-print";
import { jamboree2027PreviewLogo } from "../../assets";
import Heading from "../../component-lib/Atoms/heading/EventEHRHeading";
import Table from "../../component-lib/Organisms/table/EventEHRTable";
import EventEHRCheckbox from "../../component-lib/Molecules/checkbox/EventEHRCheckbox";
import Button from "../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../text-constants/textConstants";
import { useDispatch, useSelector } from "react-redux";
import { componentKey } from "../../screens/patient-screen/patientSlice";
import { formatPhoneNumber } from "../phoneNumberFormatter";
import moment from "moment";
import {
  patchVisitNoteData,
  postVisitNoteData,
  sendVisitNote,
} from "../../screens/patient-screen/patientSaga";
import { useLocation, useNavigate } from "react-router-dom";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getSortedSceneSummeryData } from "../../screens/patient-screen/jot-form/constant";
import { isOffsiteTypeOfCare } from "../offsite/isOffsite";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    width: "650px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

const headerTitleFrontStyle = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#393939",
  marginBottom: "10px",
};

interface DischargeFormPreviewModelProps {
  open: boolean;
  visitNoteId?: string;
  patientId?: string;
  comesFrom?: string;
  selectedDispositionKey?: string;
  signVisit?: boolean;
  dischargeNote?: string;
  handleCloseModel: (event: any) => void;
}

export default function DischargeFormPreviewModel({
  open = false,
  visitNoteId,
  patientId,
  selectedDispositionKey,
  signVisit = false,
  comesFrom,
  handleCloseModel,
  dischargeNote,
}: DischargeFormPreviewModelProps) {
  const commonHeadingStyle = { color: "#004981", marginBottom: "16px" };
  const accordionBgColor = { backgroundColor: "#85c3f3" };
  const fullWidth = { width: "100%" };

  const location = useLocation();

  // Get the last part of the URL
  const pageName = location?.pathname?.split("/").pop() || "Home";

  const {
    dataOfDischargeForm,
    visitNoteCareFlowData,
    dispositionData,
    notesData,
  } = useSelector((state: any) => state[componentKey]);

  // Offsite Transfers dispositions must send `offsiteTransferDisposition: true`.
  // Matches ListeningEarCareVisitFlow's offsite check (route or hospital typeOfCare).
  const isOffsiteDisposition =
    !!location?.pathname?.includes("offsite-transfers") ||
    isOffsiteTypeOfCare(dataOfDischargeForm?.typeOfCare);

  let index =
    dataOfDischargeForm?.visit_note?.findIndex(
      (obj: any) => obj.id === notesData.id,
    ) || 0;
  // Set index to 0 if not found
  if (index === -1) {
    index = dataOfDischargeForm?.visit_note?.length - 1;
  }

  const emsSceneData: any = getSortedSceneSummeryData(
    visitNoteCareFlowData?.visit_note?.[index],
    visitNoteCareFlowData,
  );

  const {
    demographics,
    disposition,
    incidentLocation,
    initialData,
    signature,
    soapAndProc,
    treatedAndRelease,
    iftSoapNote,
  } = emsSceneData || {};

  const {
    IVPlacement,
    Lead12EKG,
    MedicationAdministered,
    Other,
    Oxygen,
    airwayManagement,
    woundCare_dressing,
    Splinting,
  } = soapAndProc?.procedureDone || {};

  const { endotrachealIntubation, iCGEL } = airwayManagement || {};

  const intubationData = endotrachealIntubation;
  const iCGELData = iCGEL;

  const {
    sentToHospitalByAir,
    sentToHospitalByPOV,
    sentToHospitalByEMS,
    sentToHospitalByNSJTransportation,
  } = emsSceneData?.disposition?.dispositionData?.dispositions || {};

  // Visit note data
  const visitNote = dataOfDischargeForm?.visit_note?.[index] || {};

  // Interval notes aggregated across all visit notes (they may sit on an
  // earlier "InInterval" note rather than the discharged one).
  const intervalNotes = (dataOfDischargeForm?.visit_note || [])
    .flatMap((vn: any) => vn?.intervalNotes || [])
    .sort(
      (a: any, b: any) =>
        new Date(a?.createdAt).getTime() - new Date(b?.createdAt).getTime(),
    );
  const intervalNotesColumns = [
    {
      field: "srNo",
      label: <div></div>,
      renderLogic: (_row: any, i: any) => (
        <p className="text-neutral-90 font-normal">{i + 1}</p>
      ),
    },
    {
      field: "note",
      label: <div className="w-[40vw]">Note</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.note || "-"}</p>
      ),
    },
    {
      field: "dateTime",
      label: <div>Date &amp; Time</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.createdAt
            ? moment(row?.createdAt).format("MM/DD/YYYY hh:mm A")
            : "-"}
        </p>
      ),
    },
  ];
  // An order can bundle multiple medications/items in its item[] array, so
  // flatten to one display row per item (the columns read item[0]).
  const flattenOrderItems = (order: any) => {
    const items = order?.item?.length ? order.item : [{}];
    return items.map((it: any) => ({ ...order, item: [it] }));
  };
  const rxORowData = dataOfDischargeForm?.visit_note?.[index]?.order
    ?.filter((data: any) => data?.type === "Rx Order")
    ?.flatMap(flattenOrderItems);
  const noneRXRowData = dataOfDischargeForm?.visit_note?.[index]?.order
    ?.filter((data: any) => data?.type !== "Rx Order")
    ?.flatMap(flattenOrderItems);

  // The bundle's orders are already rendered in the Orders tables (order[]
  // carries the same Rx / Non-Rx data), so only the bundle VITALS — which don't
  // exist on order[] — are displayed here.
  const bundleOrderData = dataOfDischargeForm?.bundleOrder?.[0];
  const bundleVitalRows = bundleOrderData?.vitals || [];
  const sections = [
    { key: "historyOfPresent", title: "History Of Present Illness" },
    { key: "objectiveNote", title: "Objective Note" },
    { key: "assessmentDescription", title: "Assessment Description" },
    { key: "plan", title: "Plan" },
    { key: "subjectiveNote", title: "Subjective Note" },
    { key: "severity", title: "Severity" },
    { key: "objectiveSymptoms", title: "Objective Symptoms" },
    { key: "description", title: "Description" },
  ];

  const [selectedDiagnosis, setSelectedDiagnosis] = React.useState<any>([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const getIntubationStatus = (successful?: boolean) => {
    return successful === true ? "Yes" : successful === false ? "No" : "-";
  };
  // GET TRUTH KEY
  const getTrueKey = (route: Record<string, boolean>) => {
    return (
      Object.entries(route).find(([_, value]) => value === true)?.[0] ?? "-"
    );
  };

  const getModifiedString = (text: string) => {
    if (text) {
      return (
        text?.charAt(0)?.toUpperCase() +
        text?.substring(1)?.split("_")?.join(" ")
      );
    }
    return "-";
  };

  const patient = dataOfDischargeForm?.patient?.participant;
  const incidentDetail = dataOfDischargeForm;
  const headerTitleFrontStyle = {
    fontSize: "14px",
    fontWeight: 600,
    color: "#393939",
    marginBottom: "10px",
  };
  const labelFontStyle = {
    fontWeight: 500,
    color: "#727272",
    fontSize: "12px",
    marginRight: "8px",
  };
  const valueFontStyle = {
    fontWeight: 500,
    color: "#393939",
    fontSize: "12px",
    marginLeft: "8px",
  };
  const colonFontStyle = {
    fontWeight: "400",
    fontSize: "12px",
    color: "#393939",
  };

  const patientDetails = [
    {
      label: "Patient Name",
      value: `${patient?.firstName || "-"} ${patient?.lastName || ""}`,
    },
    { label: "BSA ID", value: patient?.bsaId || "-" },
    {
      label: "Date of Visit",
      value: incidentDetail?.createdAt
        ? moment(incidentDetail?.createdAt)?.format("MM/DD/YYYY")
        : "-",
    },
    {
      label: "Contact Number",
      value: patient?.contactNumber
        ? formatPhoneNumber(patient?.contactNumber)
        : "-",
    },
    { label: "Unit Number", value: patient?.baseCamp?.unitNumber || "-" },
    {
      label: "Treating Clinic",
      value:
        dataOfDischargeForm?.visit_note?.[index]?.createdProvider?.facility
          ?.name ||
        dataOfDischargeForm?.patient?.facility?.name ||
        "-",
    },
  ];

  if (visitNoteCareFlowData?.type === "Scene") {
    patientDetails.push(
      {
        label: "Unit Type",
        value: initialData?.unitType || "-",
      },
      {
        label: "Unit Designation",
        value: initialData?.unitDesignation || "-",
      },

      {
        label: "Date Of Birth",
        value: demographics?.dob
          ? // DOB is date-only (midnight UTC) — render in UTC so it never shifts.
            moment.utc(demographics?.dob).format("MM/DD/YYYY")
          : "",
      },
      {
        label: "Age",
        value: demographics?.age || "-",
      },
      {
        label: "Gender",
        value: demographics?.gender || "-",
      },
      {
        label: "Registration Id",
        value: demographics?.registrationId || "-",
      },
      {
        label: "Participant Type",
        value: demographics?.participantType || "-",
      },
    );
  }

  const incidentDetails = [
    {
      label: "Incident Date",
      value: incidentDetail?.incidentDate
        ? // Date-only value (midnight UTC) — render in UTC so it never shifts.
          moment.utc(incidentDetail?.incidentDate)?.format("MM/DD/YYYY")
        : "-",
    },
    { label: "Incident Time", value: incidentDetail?.incidentTime || "-" },
    {
      label: "Incident Location",
      value: incidentDetail?.incidentlocation || "-",
    },
    { label: "Witness Name", value: incidentDetail?.witnessName || "-" },
    { label: "Witness Email", value: incidentDetail?.witnessEmail || "-" },
  ];

  if (visitNoteCareFlowData?.type === "Scene") {
    incidentDetails.push({
      label: "Witness Contact Number",
      value: incidentDetail?.witnessContact
        ? formatPhoneNumber(incidentDetail?.witnessContact)
        : "-",
    });
  }

  if (visitNoteCareFlowData?.type === "Scene") {
    patientDetails.push({
      label: "Participant Type",
      value: demographics?.participantType || "-",
    });
  }

  const ICDCodeColumns = [
    {
      field: "srNo",
      label: <div>ICD 10 Code</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.label}</p>;
      },
    },
    {
      field: "details",
      label: <div className="">Details</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.description}</p>
        );
      },
    },
  ];

  // BUNDLE ORDER COLUMNS
  // Bundle Vitals table columns
  const bundleVitalColumns = [
    {
      field: "vital",
      label: <div>Vital</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.vitalName}</p>
      ),
    },
    {
      field: "frequency",
      label: <div>Frequency</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.frequency || "-"}</p>
      ),
    },
  ];

  // RX ORDER COLUMNS
  const RXOrderColumns = [
    {
      field: "srNo",
      label: <div></div>,
      renderLogic: (row: any, index: any) => {
        return <p className="text-neutral-90 font-normal">{index + 1}</p>;
      },
    },
    {
      field: "condition",
      label: <div className="w-[10vw]">Condition</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.item?.[0]?.conditionName ||
              dataOfDischargeForm?.chiefComplaints ||
              "-"}
          </p>
        );
      },
    },
    {
      field: "medication",
      label: <div className="w-[10vw]">Medication</div>,
      renderLogic: (row: any) => {
        return (
          <p className=" font-normal">
            {row?.item?.[0]?.medicationName || "-"}
          </p>
        );
      },
    },
    {
      field: "qty",
      label: <div className="w-[5vw]">Qty</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.item?.[0]?.quantity || "-"}
          </p>
        );
      },
    },
    {
      field: "unit",
      label: <div className="w-[5vw]">Unit</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.item?.[0]?.unit || "-"}
          </p>
        );
      },
    },
    {
      field: "frequency",
      label: <div className="w-[5vw]">Frequency</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.item?.[0]?.frequency || row?.frequency || "-"}
          </p>
        );
      },
    },
    {
      field: "details",
      label: <div className="w-[20vw]">Details</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.note || "-"}</p>
        );
      },
    },
    {
      field: "status",
      label: <div className="w-[10vw]">Status</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.status ? row?.status?.split("_")?.join(" ") : "-"}
          </p>
        );
      },
    },
  ];
  // CKO ORDER COLUMNS
  const nonRXColumns = [
    {
      field: "srNo",
      label: <div></div>,
      renderLogic: (row: any, index: any) => {
        return <p className="text-neutral-90 font-normal">{index + 1}</p>;
      },
    },
    {
      field: "orderType",
      label: <div className="w-[10vw]">Order Type</div>,
      renderLogic: (row: any) => {
        return (
          <p className="font-normal">
            {row?.type?.split("_")?.join(" ") || "-"}
          </p>
        );
      },
    },
    {
      field: "Items",
      label: <div className="w-[10vw]">Items</div>,
      renderLogic: (row: any) => {
        return <p className="font-normal">{row?.item?.[0]?.name || "-"}</p>;
      },
    },
    {
      field: "details",
      label: <div className="w-[50vw]">Details</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.note}</p>;
      },
    },
    {
      field: "status",
      label: <div className="w-[10vw]">Status</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.status ? row?.status?.split("_")?.join(" ") : "-"}
          </p>
        );
      },
    },
  ];

  const vitalsColumns = [
    {
      field: "Pulse",
      label: <div>Pulse</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.pulse}</p>;
      },
    },
    {
      field: "SBP",
      label: <div className="">SBP</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.SBP}</p>;
      },
    },
    {
      field: "DBP",
      label: <div className="">DBP</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.DBP}</p>;
      },
    },
    {
      field: "Resp Rate",
      label: <div>Resp Rate</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.respirationRate}</p>
        );
      },
    },

    {
      field: "SpO2",
      label: <div>SpO2</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.SPO2}</p>;
      },
    },
    {
      field: "GCS",
      label: <div className="">GCS</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.GCS}</p>;
      },
    },
    {
      field: "Time",
      label: <div className="">Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const iVInsertionColumns = [
    {
      field: "Size",
      label: <div>Size</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.size}</p>;
      },
    },
    {
      field: "Attempts",
      label: <div className="">Attempts</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.attempts}</p>;
      },
    },
    {
      field: "Successful",
      label: <div className="">Successful</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.successful}</p>;
      },
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const medicationAdministeredColumns = [
    {
      field: "Medication",
      label: <div>Medication</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.medication}</p>;
      },
    },
    {
      field: "Dose (mg)",
      label: <div className="">Dose (mg)</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.dose}</p>;
      },
    },
    {
      field: "Route",
      label: <div className="">Route</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.route}</p>;
      },
    },
    {
      field: "Response",
      label: <div>Response</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.response}</p>;
      },
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const oxygenColumns = [
    {
      field: "Device",
      label: <div>Device</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.device}</p>;
      },
    },
    {
      field: "LPM",
      label: <div className="">LPM</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.LPM}</p>;
      },
    },
    {
      field: "Improvement",
      label: <div>Improvement</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.improvement}</p>
        );
      },
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const iGelInsertionColumn = [
    {
      field: "Size",
      label: <div>Size</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.size}</p>;
      },
    },
    {
      field: "Attempts",
      label: <div className="">Attempts</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.attempts}</p>;
      },
    },
    {
      field: "Successful",
      label: <div className="">successful</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.successful}</p>;
      },
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const endIntColumn = [
    {
      field: "Attempts",
      label: <div>Attempts</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.attempts}</p>;
      },
    },
    {
      field: "Blade Size",
      label: <div className="">Blade Size</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.bladeSize}</p>;
      },
    },
    {
      field: "Device",
      label: <div className="">Device</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.device}</p>;
      },
    },
    {
      field: "Tube Size",
      label: <div className="">Tube Size</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.tubeSize}</p>;
      },
    },
    {
      field: "Successful",
      label: <div className="">successful</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.successful}</p>;
      },
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const endIntRowData = [
    {
      attempts: intubationData?.attempts || "-",
      bladeSize: intubationData?.bladeSize || "-",
      device: intubationData?.device || "-",
      tubeSize: intubationData?.tubeSize || "-",
      successful: getIntubationStatus(intubationData?.successful),
      time: intubationData?.time || "-",
    },
  ];

  const iGelInsertionRowData = [
    {
      size: iCGELData?.size || "-",
      attempts: iCGELData?.attempts || "-",
      successful: getIntubationStatus(iCGELData?.successful),
      time: iCGELData?.time || "-",
    },
  ];

  const vitalsRowData = emsSceneData?.soapAndProc?.vitals?.map(
    (data: any, index: number) => ({
      pulse: data?.heartRate || "-",
      SBP: data?.systolic || "-",
      DBP: data?.diastolic || "-",
      respirationRate: data?.respirationRate || "-",
      SPO2: data?.oxygenSaturation || "-",
      GCS: data?.GCS || "-",
      time: data?.time || "-",
    }),
  );

  const oxygenRowData = Oxygen?.[0]?.IVs?.map((data: any, index: number) => ({
    device: data?.device || "-",
    LPM: data?.LPM || "-",
    DBP: data?.DBP || "-",
    improvement: data?.improvement || "-",
    time: data?.time || "-",
  }));

  const medicationAdministeredColumnsRowData =
    MedicationAdministered?.[0]?.meds?.map((data: any, index: number) => ({
      medication: data?.medication || "-",
      dose: data?.dose || "-",
      route: getTrueKey(data?.route),
      response: data?.response?.Improve
        ? "Improve"
        : data?.response?.nochange
          ? "No Change"
          : data?.response?.worsen
            ? "Worsen"
            : "-",
      time: data?.response?.time || "-",
    }));

  const iVInsertionRowData = IVPlacement?.[0]?.data?.map(
    (data: any, index: number) => ({
      size: data?.size || "-",
      attempts: data?.attempts || "-",
      successful: data?.successful || "-",
      time: data?.time || "-",
    }),
  );

  const rowData =
    comesFrom === "viewVisitNote"
      ? selectedDiagnosis
      : dataOfDischargeForm?.assessment;

  const contentRef = React.useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });
  const [isCheckboxSelected, setIsCheckboxSelected] = React.useState(false);
  // Email/SMS send options (Patient, Unit Leader, Emergency Contact) - shared by both modes
  const [sendPatientEmail, setSendPatientEmail] = React.useState(false);
  const [sendPatientSms, setSendPatientSms] = React.useState(false);
  const [sendUnitLeaderEmail, setSendUnitLeaderEmail] = React.useState(false);
  const [sendUnitLeaderSms, setSendUnitLeaderSms] = React.useState(false);
  const [sendEmergencyEmail, setSendEmergencyEmail] = React.useState(false);
  const [sendEmergencySms, setSendEmergencySms] = React.useState(false);

  const handleClose = () => {
    handleCloseModel(false);
  };

  // UPDATE DRAFT DATA IF EXISTS
  React.useEffect(() => {
    setSelectedDiagnosis(
      visitNoteCareFlowData?.visit_note?.[index]?.assessment?.map(
        (data: any, index: number) => ({
          id: data?.code,
          index: index,
          label: data?.code,
          value: data?.code,
          description: data?.desc,
        }),
      ) || [],
    );
  }, [visitNoteCareFlowData]);

  const handleDischargeClick = () => {
    const isEmpty = (obj: any) => Object.keys(obj)?.length === 0;

    // Only include email/SMS parameters for non-Self_Care visits
    const isSelfCare = dataOfDischargeForm?.typeOfCare === "Self_Care";
    const sendNotificationParams = isSelfCare
      ? {}
      : {
          sendPatientEmail: sendPatientEmail || false,
          sendPatientSms: sendPatientSms || false,
          sendUnitLeaderEmail: sendUnitLeaderEmail || false,
          sendUnitLeaderSms: sendUnitLeaderSms || false,
          sendEmergencyEmail: sendEmergencyEmail || false,
          sendEmergencySms: sendEmergencySms || false,
        };

    const postApiPayload = {
      apiPayload: {
        patientId: patientId,
        visitId: visitNoteId,
        assessment: !isEmpty(dataOfDischargeForm?.assessment)
          ? dataOfDischargeForm?.assessment?.map((data: any) => ({
              code: data?.label,
              desc: data?.description,
            }))
          : undefined,
        isVisitSign: true,
        acknowledge: true,
        status: "discharged",
        ...(isOffsiteDisposition && { offsiteTransferDisposition: true }),
        historyOfPresent:
          dataOfDischargeForm?.fieldData?.presentIllnessHistory || undefined,
        description: dataOfDischargeForm?.fieldData?.description || undefined,
        plan: dataOfDischargeForm?.fieldData?.plan || undefined,
        // objectiveNote: ,
        subjectiveNote:
          dataOfDischargeForm?.fieldData?.subjectiveNote || undefined,
        // objectiveSymptoms: "Pale complexion and slow reflexes observed.",
        ...sendNotificationParams,
      },
      comesFrom: "dischargeButton",
      patientId: patientId,
      visitNoteId: visitNoteId,
      navigate,
    };

    const patchApiPayload = {
      apiPayload: {
        status: "discharged",
        acknowledge: true,
        isVisitSign: true,
        ...(isOffsiteDisposition && { offsiteTransferDisposition: true }),
        assessment: !isEmpty(dataOfDischargeForm?.assessment)
          ? dataOfDischargeForm?.assessment?.map((data: any) => ({
              code: data?.label,
              desc: data?.description,
            }))
          : undefined,
        dischargeNote: dischargeNote || undefined,
        ...sendNotificationParams,
        historyOfPresent:
          dataOfDischargeForm?.fieldData?.presentIllnessHistory || undefined,
        description: dataOfDischargeForm?.fieldData?.description || undefined,
        plan: dataOfDischargeForm?.fieldData?.plan || undefined,
      },
      comesFrom: "dischargeButton",
      visitNoteId: dataOfDischargeForm?.visit_note?.[index]?.id,
      patientId: patientId,
      navigate,
    };

    try {
      if (!visitNoteCareFlowData?.visit_note?.[index]?.assessment?.length) {
        if (
          dataOfDischargeForm?.typeOfCare?.toLowerCase()?.includes("ear") ||
          isOffsiteTypeOfCare(dataOfDischargeForm?.typeOfCare)
        ) {
          dispatch(patchVisitNoteData(patchApiPayload));
        } else {
          dispatch(postVisitNoteData(postApiPayload));
        }
      } else {
        dispatch(patchVisitNoteData(patchApiPayload));
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  // Reset all email/sms send options
  const resetSendOptions = () => {
    setSendPatientEmail(false);
    setSendPatientSms(false);
    setSendUnitLeaderEmail(false);
    setSendUnitLeaderSms(false);
    setSendEmergencyEmail(false);
    setSendEmergencySms(false);
  };

  // VIEW NOTE: send already-discharged note via the send-note endpoint
  const isAnySendOptionSelected =
    sendPatientEmail ||
    sendPatientSms ||
    sendUnitLeaderEmail ||
    sendUnitLeaderSms ||
    sendEmergencyEmail ||
    sendEmergencySms;

  const handleSendNote = () => {
    dispatch(
      sendVisitNote({
        apiPayload: {
          sendPatientEmail,
          sendPatientSms,
          sendUnitLeaderEmail,
          sendUnitLeaderSms,
          sendEmergencyEmail,
          sendEmergencySms,
        },
        visitNoteId: visitNoteCareFlowData?.visit_note?.[index]?.id,
        onSuccess: resetSendOptions,
      }),
    );
  };

  // Email/SMS send options, shared by discharge & view-note modes.
  const renderSendOptions = () => {
    const optionLabelStyle = {
      fontSize: "12px",
      fontWeight: 500,
      color: "#979797",
    };
    const recipientInlineStyle: React.CSSProperties = {
      fontSize: "12px",
      fontWeight: 500,
      color: "#979797",
    };
    return (
      <div className="pt-3 pl-4 pb-4 pr-4 important-content">
        <div
          style={{
            fontSize: "12px",
            fontWeight: 600,
            marginBottom: "12px",
          }}
        >
          Optional - Send Discharge Form
        </div>
        {/* Patient */}
        <div
          className="flex items-center"
          style={{ gap: "24px", marginBottom: "10px" }}
        >
          <EventEHRCheckbox
            checkboxSelected={sendPatientEmail || sendPatientSms}
            label="Send Discharge Form to Patient –"
            customLabelStyle={recipientInlineStyle}
            callback={() => {
              if (sendPatientEmail || sendPatientSms) {
                setSendPatientEmail(false);
                setSendPatientSms(false);
              } else {
                setSendPatientEmail(true);
                setSendPatientSms(true);
              }
            }}
          />
          <EventEHRCheckbox
            checkboxSelected={sendPatientEmail}
            label="Email"
            customLabelStyle={optionLabelStyle}
            callback={() => setSendPatientEmail(!sendPatientEmail)}
          />
          <EventEHRCheckbox
            checkboxSelected={sendPatientSms}
            label="SMS"
            customLabelStyle={optionLabelStyle}
            callback={() => setSendPatientSms(!sendPatientSms)}
          />
        </div>

        {/* Unit Leader */}
        <div
          className="flex items-center"
          style={{ gap: "24px", marginBottom: "10px" }}
        >
          <EventEHRCheckbox
            checkboxSelected={sendUnitLeaderEmail || sendUnitLeaderSms}
            label="Send Discharge Form to Unit Leader –"
            customLabelStyle={recipientInlineStyle}
            callback={() => {
              if (sendUnitLeaderEmail || sendUnitLeaderSms) {
                setSendUnitLeaderEmail(false);
                setSendUnitLeaderSms(false);
              } else {
                setSendUnitLeaderEmail(true);
                setSendUnitLeaderSms(true);
              }
            }}
          />
          <EventEHRCheckbox
            checkboxSelected={sendUnitLeaderEmail}
            label="Email"
            customLabelStyle={optionLabelStyle}
            callback={() => setSendUnitLeaderEmail(!sendUnitLeaderEmail)}
          />
          <EventEHRCheckbox
            checkboxSelected={sendUnitLeaderSms}
            label="SMS"
            customLabelStyle={optionLabelStyle}
            callback={() => setSendUnitLeaderSms(!sendUnitLeaderSms)}
          />
        </div>

        {/* Emergency Contact */}
        <div className="flex items-center" style={{ gap: "24px" }}>
          <EventEHRCheckbox
            checkboxSelected={sendEmergencyEmail || sendEmergencySms}
            label="Send Discharge Form to Emergency Contact –"
            customLabelStyle={recipientInlineStyle}
            callback={() => {
              if (sendEmergencyEmail || sendEmergencySms) {
                setSendEmergencyEmail(false);
                setSendEmergencySms(false);
              } else {
                setSendEmergencyEmail(true);
                setSendEmergencySms(true);
              }
            }}
          />
          <EventEHRCheckbox
            checkboxSelected={sendEmergencyEmail}
            label="Email"
            customLabelStyle={optionLabelStyle}
            callback={() => setSendEmergencyEmail(!sendEmergencyEmail)}
          />
          <EventEHRCheckbox
            checkboxSelected={sendEmergencySms}
            label="SMS"
            customLabelStyle={optionLabelStyle}
            callback={() => setSendEmergencySms(!sendEmergencySms)}
          />
        </div>
      </div>
    );
  };

  const updateStatus = (status: any) => {
    return status === "transferOffProperty"
      ? "transferOffProperty"
      : status === "transferToClinic"
        ? "transferToClinic"
        : status === "transferToJHC"
          ? "transferToJHC"
          : "discharged";
  };

  const handleSubmitClick = () => {
    const payload = {
      apiPayload: {
        status: signVisit
          ? updateStatus(visitNoteCareFlowData?.status)
          : updateStatus(dispositionData?.status),
        acknowledge: true,
        isVisitSign: true,
      },
      comesFrom:
        pageName === "dashboard"
          ? "dashboard"
          : pageName === "unsigned-visits"
            ? "unsigned-visits"
            : "dischargeButton",
      visitNoteId: signVisit
        ? visitNoteCareFlowData?.visit_note?.[index]?.id
        : dispositionData?.visitNoteId,
      patientId: patientId,
      navigate,
    };
    dispatch(patchVisitNoteData(payload));
  };

  return (
    <React.Fragment>
      <BootstrapDialog
        // onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <div
          className="flex justify-between items-center"
          style={{ padding: "12px 16px" }}
        >
          <div
            // sx={{ m: 0, p: 2, visibility: "hidden" }}
            className="font-weight-500 font-size-18px"
            style={{ visibility: "hidden" }}
          >
            Modal title
          </div>
          {comesFrom !== "soapNote" && (
            <div>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
            </div>
          )}
        </div>

        <DialogContent dividers>
          <div
            ref={contentRef}
            style={{
              //   width: "42%",
              // textAlign: "center",
              //   position: "relative",
              //   left: "25%",
              //   border: "1px solid red",
              height: "-webkit-fill-available",
              overflow: "auto",
            }}
          >
            {/* Heading */}
            <header className="flex items-center justify-between p-3">
              <div className="flex items-center gap-2">
                <img src={jamboree2027PreviewLogo} alt="jamboree logo" />
                <div className="font-weight-900 font-size-20px color-393939">
                  JAMBOREE 2026
                </div>
              </div>

              <div className="font-weight-650 font-size-18px color-005596">
                JamboCare
              </div>
            </header>
            {/* Type */}
            <div className="background-005596 color-FFFFFF font-weight-500 font-size-14px text-align-center p-1">
              {dataOfDischargeForm?.typeOfCare === "Self_Care"
                ? "Self Care"
                : dataOfDischargeForm?.typeOfCare
                      ?.toLowerCase()
                      ?.includes("nurse")
                  ? "Nurse Care"
                  : dataOfDischargeForm?.typeOfCare
                        ?.toLowerCase()
                        ?.includes("mental")
                    ? "Mental Health Care"
                    : dataOfDischargeForm?.typeOfCare
                          ?.toLowerCase()
                          ?.includes("ear")
                      ? "Listening Ear Care"
                      : dataOfDischargeForm?.typeOfCare === "Physician"
                        ? "Physician Care"
                        : dataOfDischargeForm?.typeOfCare === null
                          ? "EMS Scene "
                          : dataOfDischargeForm?.typeOfCare}{" "}
              Visit
            </div>
            {/* Patient Details */}
            <div className="pt-3 pl-4 pb-4 pr-4">
              <Heading
                title="Patient Details"
                customStyle={headerTitleFrontStyle}
              />
              {/* <div>Patient Details</div> */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  rowGap: "10px",
                }}
              >
                {patientDetails?.map((data, index) => (
                  <div key={index}>
                    <span style={labelFontStyle}>{data?.label}</span>
                    <span style={colonFontStyle}>:</span>
                    <span style={valueFontStyle}>{data?.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <hr />
            {/* Incident Details */}
            <div className="pt-3 pl-4 pb-4 pr-4">
              <Heading
                title="Incident Details"
                customStyle={headerTitleFrontStyle}
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  rowGap: "10px",
                }}
              >
                {incidentDetails?.map((data, index) => (
                  <div key={index}>
                    <span style={labelFontStyle}>{data?.label}</span>
                    <span style={colonFontStyle}>:</span>
                    <span style={valueFontStyle}>{data?.value}</span>
                  </div>
                ))}
              </div>
              {visitNoteCareFlowData?.type === "Scene" && (
                <div className="pt-5 pb-4 pr-4">
                  <Heading
                    title="BRIEF factual description of incident:"
                    customStyle={labelFontStyle}
                  />
                  <div
                    style={{ ...valueFontStyle, marginLeft: 0 }}
                    className="font-weight-500 color-979797 font-size-12px mt-2"
                  >
                    {incidentDetail?.chiefComplaints || "-"}
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Chief Complaint */}
            {visitNoteCareFlowData?.type !== "Scene" && (
              <div className="pt-3 pl-4 pb-4 pr-4">
                <Heading
                  title="Chief Complaint"
                  customStyle={headerTitleFrontStyle}
                />
                <div className="font-weight-500 color-979797 font-size-12px">
                  {dataOfDischargeForm?.chiefComplaints}
                </div>
              </div>
            )}
            <hr />
            {/* data */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr" }}>
              {sections?.map(
                ({ key, title }) =>
                  visitNote[key] && (
                    <VisitNoteSection
                      key={key}
                      title={title}
                      content={visitNote[key]}
                    />
                  ),
              )}
            </div>

            {/* Interval Notes (aggregated across the visit's notes) */}
            {intervalNotes?.length > 0 && (
              <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                <Heading
                  title="Interval Notes"
                  customStyle={headerTitleFrontStyle}
                />
                <div className="mt-2">
                  <Table
                    columns={intervalNotesColumns}
                    rows={intervalNotes}
                  />
                </div>
              </div>
            )}

            {/* Encounter Topic & Resolution — Listening Ear only */}
            {dataOfDischargeForm?.typeOfCare?.toLowerCase()?.includes("ear") && (
              <>
                {visitNote?.encounterTopic?.length > 0 && (
                  <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                    <Heading
                      title="Encounter Topic"
                      customStyle={headerTitleFrontStyle}
                    />
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {visitNote.encounterTopic.map((topic: string) => (
                        <span
                          key={topic}
                          style={{
                            backgroundColor: "#EAF2FB",
                            color: "#005596",
                            borderRadius: "16px",
                            padding: "4px 12px",
                            fontSize: "12px",
                            fontWeight: 500,
                            WebkitPrintColorAdjust: "exact",
                            printColorAdjust: "exact",
                          }}
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {visitNote?.resolution && (
                  <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                    <Heading
                      title="Resolution"
                      customStyle={headerTitleFrontStyle}
                    />
                    <div className="font-weight-500 color-979797 font-size-12px">
                      {visitNote.resolution}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Diagnosis — hidden for Listening Ear and Offsite Transfers */}
            {!dataOfDischargeForm?.typeOfCare?.toLowerCase()?.includes("ear") &&
              !isOffsiteTypeOfCare(dataOfDischargeForm?.typeOfCare) && (
                <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                  <Heading
                    title="Diagnosis"
                    customStyle={headerTitleFrontStyle}
                  />
                  <div>
                    <Table columns={ICDCodeColumns} rows={rowData} />
                  </div>
                </div>
              )}

            {/* Bundle Order Vitals (orders themselves render in the Orders
                    tables; only the bundle vitals are unique to the bundle) */}
            {bundleVitalRows.length > 0 && (
              <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                <Heading
                  title="Bundle Order Vitals"
                  customStyle={headerTitleFrontStyle}
                />
                <div className="mt-2">
                  <Table columns={bundleVitalColumns} rows={bundleVitalRows} />
                </div>
              </div>
            )}

            {(dischargeNote ||
              visitNoteCareFlowData?.visit_note?.[index]?.dischargeNote) && (
              <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                <Heading
                  title="Discharge Instructions"
                  customStyle={headerTitleFrontStyle}
                />
                <div
                  className="font-weight-500 color-979797 font-size-12px"
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "12px 16px",
                    borderRadius: "6px",
                    border: "1px solid #e9ecef",
                    lineHeight: "1.5",
                    whiteSpace: "pre-wrap",
                    // color: "green",
                    color: "rgb(57, 57, 57)",
                  }}
                >
                  {dischargeNote ||
                    visitNoteCareFlowData?.visit_note?.[index]?.dischargeNote}
                </div>
              </div>
            )}

            {rxORowData?.length > 0 && (
              <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                <Heading
                  title="Rx Order"
                  customStyle={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#-",
                    marginBottom: "8px",
                  }}
                />

                <Table
                  columns={RXOrderColumns}
                  rows={rxORowData}
                  noDataFoundMessage={"No ICD 10 or DSM Code entered"}
                />
              </div>
            )}
            {noneRXRowData?.length > 0 && (
              <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                <Heading
                  title="Non Rx Order"
                  customStyle={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#-",
                    marginBottom: "8px",
                  }}
                />

                <Table
                  columns={nonRXColumns}
                  rows={noneRXRowData}
                  noDataFoundMessage={"No Data"}
                />
              </div>
            )}

            {/* ACCORDION */}
            {visitNoteCareFlowData?.type === "Scene" &&
              !visitNoteCareFlowData?.visit_note?.[index]?.assessment && (
                <div className="mt-8 flex flex-col gap-3 ">
                  {/*   SOAP and Procedures
                   */}
                  {/* {unitType !== "mf_ito" && ( */}
                  {visitNoteCareFlowData?.unitType !==
                    "Military FLA-Interfacility Transport_ONLY" && (
                    <Accordion
                      className="important-content"
                      expanded
                      sx={accordionBgColor}
                      style={fullWidth}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        SOAP and Procedures
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: "white" }}>
                        {/* Vital Signs Section */}
                        <div className="p-3">
                          <div className="w-[60%] pb-3">
                            <Heading
                              title="Chief Complaint"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="font-weight-500 color-979797 font-size-12px">
                              {emsSceneData?.soapAndProc?.chiefComplaints}
                            </div>
                          </div>

                          <hr />

                          <div className="pt-5 pb-3">
                            <Heading
                              title="Subjective"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="font-weight-500 color-979797 font-size-12px">
                              {emsSceneData?.soapAndProc?.subjectiveNote}
                            </div>
                          </div>

                          <hr />

                          <div className="mt-5">
                            <Heading
                              title="Vital Signs - Please Scroll To see all fields"
                              customStyle={headerTitleFrontStyle}
                            />
                            <Table
                              columns={vitalsColumns}
                              rows={vitalsRowData}
                              noDataFoundMessage={"No Data"}
                            />
                          </div>
                        </div>

                        <hr />

                        <div className="pt-5 pb-3">
                          <Heading
                            title="Objective/Exam Findings"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {emsSceneData?.soapAndProc?.objectiveNote}
                          </div>
                        </div>
                        <hr />

                        <div className="pt-5  pb-3">
                          <Heading
                            title="Assessment and Plan & Treatment"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {emsSceneData?.soapAndProc?.assessment}
                          </div>
                        </div>
                        <hr />

                        <div className="pt-5">
                          <Heading
                            title="Procedures Done"
                            customStyle={headerTitleFrontStyle}
                          />

                          <div className="flex flex-col gap-2">
                            {airwayManagement?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Airway Management
                              </div>
                            )}
                            {woundCare_dressing?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Wound Care/Dressing
                              </div>
                            )}
                            {IVPlacement?.[0]?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                IV Placement
                              </div>
                            )}
                            {MedicationAdministered?.[0]?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Medication Administered
                              </div>
                            )}

                            {Lead12EKG?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                12 Lead EKG
                              </div>
                            )}
                            {Splinting?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Splinting
                              </div>
                            )}
                            {Oxygen?.[0]?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Oxygen
                              </div>
                            )}
                            {Other?.select && (
                              <div className="font-weight-500 color-979797 font-size-12px">
                                Other
                              </div>
                            )}
                            {Other?.select && (
                              <div className="mt-5 pb-3 ">
                                <Heading
                                  title="Other Procedure Done"
                                  customStyle={headerTitleFrontStyle}
                                />
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  {Other?.description || "-"}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <hr />

                        {/* If Airway Management Selected */}
                        {airwayManagement?.select && (
                          <div className="pt-5 pb-3">
                            <Heading
                              title="Airway Management"
                              customStyle={headerTitleFrontStyle}
                            />

                            <div className="flex flex-col gap-2">
                              {airwayManagement?.bvm && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  BVM
                                </div>
                              )}
                              {airwayManagement?.suctioning && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  Suctioning
                                </div>
                              )}
                              {airwayManagement?.OPA && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  OPA
                                </div>
                              )}
                              {airwayManagement?.NPA && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  NPA
                                </div>
                              )}

                              {iCGEL?.select && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  iGel
                                </div>
                              )}
                              {endotrachealIntubation?.select && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  Endotracheal Intubation
                                </div>
                              )}
                              {airwayManagement?.other?.select && (
                                <div className="font-weight-500 color-979797 font-size-12px">
                                  Other
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <hr />

                        {/* AM other */}
                        {airwayManagement?.select &&
                          airwayManagement?.other?.select && (
                            <>
                              <div className="mt-5 pb-3">
                                <Heading
                                  title="Other Airway Management"
                                  customStyle={headerTitleFrontStyle}
                                />
                                <div className="flex gap-8 mb-2">
                                  <div className="font-weight-500 color-979797 font-size-12px">
                                    {airwayManagement?.other?.desription || "-"}
                                  </div>
                                </div>
                              </div>
                              <hr />
                            </>
                          )}

                        {/* AM Endotracheal Intubation */}
                        {airwayManagement?.select &&
                          endotrachealIntubation?.select && (
                            <>
                              <div className="mt-5">
                                <Heading
                                  title="Endotracheal Intubation"
                                  customStyle={headerTitleFrontStyle}
                                />

                                <Table
                                  columns={endIntColumn}
                                  rows={endIntRowData}
                                  noDataFoundMessage={"No Data"}
                                />
                              </div>
                              <hr />
                            </>
                          )}

                        {/* AM iGel Insertion */}
                        {airwayManagement?.select && iCGEL?.select && (
                          <>
                            <div className="mt-5 pb-3">
                              <Heading
                                title="iGel Insertion"
                                customStyle={headerTitleFrontStyle}
                              />
                              <Table
                                columns={iGelInsertionColumn}
                                rows={iGelInsertionRowData}
                                noDataFoundMessage={"No Data"}
                              />
                            </div>
                            <hr />
                          </>
                        )}

                        {/* AM IV Insertion
                         */}
                        {IVPlacement?.[0]?.select && (
                          <>
                            <div className="mt-5">
                              <Heading
                                title="IV Insertion"
                                customStyle={headerTitleFrontStyle}
                              />
                              <Table
                                columns={iVInsertionColumns}
                                rows={iVInsertionRowData}
                                noDataFoundMessage={"No Data"}
                              />
                            </div>
                            <hr />
                          </>
                        )}

                        {/* Medications Administered
                         */}
                        {MedicationAdministered?.[0]?.select && (
                          <>
                            <div className="mt-5">
                              <Heading
                                title="Medication Administered"
                                customStyle={headerTitleFrontStyle}
                              />

                              <Table
                                columns={medicationAdministeredColumns}
                                rows={medicationAdministeredColumnsRowData}
                                noDataFoundMessage={"No Data"}
                              />
                            </div>
                            <hr />
                          </>
                        )}

                        {/* Oxygen Administration
                         */}
                        {Oxygen?.[0]?.select && (
                          <>
                            <div className="mt-5 pb-5">
                              <Heading
                                title="Oxygen Administration"
                                customStyle={headerTitleFrontStyle}
                              />

                              <Table
                                columns={oxygenColumns}
                                rows={oxygenRowData}
                                noDataFoundMessage={"No Data"}
                              />
                            </div>
                            <hr />
                          </>
                        )}

                        {Lead12EKG?.select && (
                          <div className="pt-5">
                            <Heading
                              title="12 Lead Interpretation"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="font-weight-500 color-979797 font-size-12px">
                              {emsSceneData?.soapAndProc?.procedureDone
                                ?.Lead12EKG?.lead12Interpration || "-"}
                            </div>
                          </div>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* IFT SOAP Note */}
                  {/* {unitType === "mf_ito" && ( */}
                  {visitNoteCareFlowData?.unitType ===
                    "Military FLA-Interfacility Transport_ONLY" && (
                    <Accordion
                      className="important-content"
                      expanded
                      sx={accordionBgColor}
                      style={fullWidth}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        IFT SOAP Note
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: "white" }}>
                        <div>
                          <div className="pt-5">
                            <Heading
                              title="Please include a brief SOAP note of your transport, including vital signs, if necessary."
                              customStyle={headerTitleFrontStyle}
                            />

                            <div className="font-weight-500 color-979797 font-size-12px">
                              {iftSoapNote}
                            </div>
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/*   Diagnosis and Disposition
                   */}
                  <Accordion
                    className="important-content"
                    expanded
                    sx={accordionBgColor}
                    style={fullWidth}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      Diagnosis and Disposition{" "}
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: "white" }}>
                      <div className="p-3">
                        <div className="pt-5 pb-3">
                          <Heading
                            title="Impression/Diagnosis"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {Array.isArray(
                              emsSceneData?.disposition?.dispositionData
                                ?.impressionDiagnosis,
                            )
                              ? emsSceneData?.disposition?.dispositionData?.impressionDiagnosis?.join(
                                  ", ",
                                )
                              : getModifiedString(
                                  emsSceneData?.disposition?.dispositionData
                                    ?.impressionDiagnosis,
                                )}
                          </div>
                        </div>
                        <hr />

                        <div className="pt-5">
                          <Heading
                            title="Transferring Unit"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {getModifiedString(
                              emsSceneData?.disposition?.dispositionData
                                ?.transferringUnit,
                            )}
                          </div>
                        </div>
                      </div>
                    </AccordionDetails>
                  </Accordion>

                  {/* Treat and Release */}
                  {/* {emsSceneData?.disposition?.dispositionData
                                      ?.dispositions?.treatedAndReleased?.select  && ( */}
                  {emsSceneData?.disposition?.dispositionData?.dispositions
                    ?.treatedAndReleased?.select && (
                    <Accordion
                      className="important-content"
                      expanded
                      sx={accordionBgColor}
                      style={fullWidth}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        Treat and Release
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: "white" }}>
                        <div className="p-3 text-[#004981]">
                          <h3 style={headerTitleFrontStyle}>
                            I, the treating EMS Clinician, certify that this
                            individual is greater that 18 years of age, or in
                            the care of someone 18 years of age or older. If
                            more than scout rendered first aid was provided AND
                            the patient is a minor with no adult is present, the
                            scout must be taken to a medical facility to be
                            evaluated and released by a physician.
                          </h3>
                          <div className="pt-3"></div>
                        </div>
                        <div className="pt-5 pb-5">
                          <hr />
                        </div>

                        {/* {treatAndReleaseChecked?.isSelected && ( */}
                        {true && (
                          <div className="p-3 text-[#004981]">
                            <h3 style={headerTitleFrontStyle}>
                              Neither I nor the attending EMT(s) and/or
                              paramedic(s) believe that I, my child, or the
                              scout I am supervising have an illness or injury
                              requiring EMS transport to a Jamboree Medical
                              Facility. I agree the the provided care is
                              sufficient for the injury, as we understand it at
                              this time. I agree that the patient does not need
                              further EMS assessment, treatment or
                              transportation at this time. I understand that if
                              I change my mind or if my condition changes and I
                              wish further treatment / transportation by the
                              Emergency Medical Services (EMS) system; I can
                              call the SOC for assistance or present at any
                              Jamboree Medical Facility.Neither I nor the
                              attending EMT(s) and/or paramedic(s) believe that
                              I, my child, or the scout I am supervising have an
                              illness or injury requiring EMS transport to a
                              Jamboree Medical Facility. I agree the the
                              provided care is sufficient for the injury, as we
                              understand it at this time. I agree that the
                              patient does not need further EMS assessment,
                              treatment or transportation at this time. I
                              understand that if I change my mind or if my
                              condition changes and I wish further treatment /
                              transportation by the Emergency Medical Services
                              (EMS) system; I can call the SOC for assistance or
                              present at any Jamboree Medical Facility.
                            </h3>

                            <div className="w-[30%] pt-7">
                              <Heading
                                title="Treat and Release Signature"
                                customStyle={commonHeadingStyle}
                              />
                              <div className="pt-3 pb-4 pr-4 w-max">
                                <div
                                  style={{
                                    display: "flex, border:'1px solid red",
                                    background: "#F4F4F4",
                                    padding: "10px 20px",
                                  }}
                                  className="text-center border-radius-8px"
                                >
                                  <div
                                    style={{
                                      fontSize: "32px",
                                      fontFamily: "Sacramento",
                                    }}
                                  >
                                    {
                                      emsSceneData?.disposition?.dispositionData
                                        ?.dispositions?.treatedAndReleased?.name
                                    }
                                  </div>
                                  <div className="font-weight-500 font-size-12px color-565656 ">
                                    Signature
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Refusal of Medical Aid/Transport */}
                  {/* {disposition === "refusal_medical_aid_transport" && ( */}
                  {emsSceneData?.disposition?.dispositionData?.dispositions
                    ?.refusalOfMedicalAid && (
                    <Accordion
                      className="important-content"
                      expanded
                      sx={accordionBgColor}
                      style={fullWidth}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        Refusal of Medical Aid/Transport
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: "white" }}>
                        <div className="p-3 text-[#004981]">
                          <h3 style={headerTitleFrontStyle}>
                            I hereby acknowledge and release the above-mentioned
                            emergency service provider and its personnel, Summit
                            Bechtel Reserve, the BSA, and all EMS system
                            physicians, nurses, agents and personnel of any
                            liability for my refusal to accept further
                            treatment. I acknowledge that I have been advised of
                            and understand the risks of not accepting emergency
                            treatment and/or transportation to the nearest
                            hospital facility, which I am refusing. I have been
                            advised as follows: - That I should receive
                            emergency medical treatment and transportation to a
                            hospital, which I am refusing. - That having
                            received emergency medical treatment, I am refusing
                            further aid or transport to a medical facility. - I
                            am refusing all medical assessment, treatment and
                            transport. - That my failure to seek treatment and
                            transport could result in permanent injury,
                            impairment, disability and could lead to my death. -
                            I certify that I have the ability and appreciate the
                            consequences of making decisions regarding my
                            medical treatment and the ability to reach an
                            informed decision. - I have been made aware and
                            understand that I can call Emergency Medical
                            Services again at any time and/or seek out medical
                            care at a Jamboree Medical Facility or other
                            community medical facility in the area.
                          </h3>

                          <div className="w-[30%] pt-7">
                            <Heading
                              title="RMA Release:"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="pt-3 pb-4 pr-4">
                              <div
                                style={{
                                  display: "flex, border:'1px solid red",
                                  background: "#F4F4F4",
                                  padding: "10px 20px",
                                }}
                                className="text-center border-radius-8px"
                              >
                                <div
                                  style={{
                                    fontSize: "32px",
                                    fontFamily: "Sacramento",
                                  }}
                                >
                                  {
                                    emsSceneData?.disposition
                                      ?.refusalOfMedicalAid?.rmaRelease
                                  }
                                </div>
                                <div className="font-weight-500 font-size-12px color-565656 ">
                                  Signature
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <hr />
                        <div>
                          <div className="w-[30%] pt-7">
                            <Heading
                              title="Witness Signature"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="pt-3 pb-4 pr-4">
                              <div
                                style={{
                                  display: "flex, border:'1px solid red",
                                  background: "#F4F4F4",
                                  padding: "10px 20px",
                                }}
                                className="text-center border-radius-8px"
                              >
                                <div
                                  style={{
                                    fontSize: "32px",
                                    fontFamily: "Sacramento",
                                  }}
                                >
                                  {
                                    emsSceneData?.disposition
                                      ?.refusalOfMedicalAid?.witnessName
                                  }
                                </div>
                                <div className="font-weight-500 font-size-12px color-565656 ">
                                  Signature
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Off Site Transfer */}

                  {(sentToHospitalByAir ||
                    sentToHospitalByPOV ||
                    sentToHospitalByEMS ||
                    sentToHospitalByNSJTransportation) && (
                    <Accordion
                      className="important-content"
                      expanded
                      sx={accordionBgColor}
                      style={fullWidth}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        Off Site Transfer{" "}
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: "white" }}>
                        <div className="w-[30%] pt-3 pb-3">
                          <Heading
                            title="Hospital/Off Site Facility"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {getModifiedString(
                              emsSceneData?.disposition?.hospitalName,
                            )}
                          </div>
                        </div>

                        <hr />
                        <div className="w-[30%] pt-5">
                          <Heading
                            title="Transferring Unit"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {getModifiedString(
                              emsSceneData?.disposition?.transferringUnit,
                            )}
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Signature */}
                  <Accordion
                    className="important-content"
                    expanded
                    sx={accordionBgColor}
                    style={fullWidth}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      Signature
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: "white" }}>
                      <div>
                        <div className="pt-5">
                          <Heading
                            title="Time Call End"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {emsSceneData?.signature?.timeCallEnd || "-"}
                          </div>
                        </div>

                        <div className="pt-5">
                          <Heading
                            title="Primary Treating Clinician"
                            customStyle={headerTitleFrontStyle}
                          />
                          <div className="font-weight-500 color-979797 font-size-12px">
                            {getModifiedString(
                              emsSceneData?.signature?.primaryClinic,
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="w-[30%] pt-7">
                        <Heading
                          title="Signature"
                          customStyle={headerTitleFrontStyle}
                        />
                        <div className="pt-3 pb-4 pr-4">
                          <div
                            style={{
                              display: "flex, border:'1px solid red",
                              background: "#F4F4F4",
                              padding: "10px 20px",
                            }}
                            className="text-center border-radius-8px"
                          >
                            <div
                              style={{
                                fontSize: "32px",
                                fontFamily: "Sacramento",
                              }}
                            >
                              {emsSceneData?.signature?.signature || "-"}{" "}
                            </div>
                            <div className="font-weight-500 font-size-12px color-565656 ">
                              Signature
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="pt-3 pb-5">
                        <hr />
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
              )}

            {/* Checkbox */}
            {comesFrom !== "viewVisitNote" && (
              <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    marginBottom: "12px",
                  }}
                >
                  Required - Attestation
                </div>

                <EventEHRCheckbox
                  checkboxSelected={isCheckboxSelected}
                  isStarPresent={true}
                  label={
                    "I confirm that all of the above care and observations have been provided and documented as per the care plan."
                  }
                  customLabelStyle={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#979797",
                  }}
                  callback={() => setIsCheckboxSelected(!isCheckboxSelected)}
                />
              </div>
            )}

            {signVisit &&
              !visitNoteCareFlowData?.visit_note?.[index]?.isVisitSign && (
                <div className="pt-3 pl-4 pb-4 pr-4 important-content">
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      marginBottom: "12px",
                    }}
                  >
                    Required - Attestation
                  </div>
                  <EventEHRCheckbox
                    checkboxSelected={isCheckboxSelected}
                    isStarPresent={true}
                    label={
                      "I confirm that all of the above care and observations have been provided and documented as per the care plan."
                    }
                    customLabelStyle={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#979797",
                    }}
                    callback={() => setIsCheckboxSelected(!isCheckboxSelected)}
                  />
                </div>
              )}

            {comesFrom !== "viewVisitNote" &&
              selectedDispositionKey === "discharged" &&
              renderSendOptions()}

            {/* Signature */}
            {comesFrom !== "viewVisitNote" &&
            (dataOfDischargeForm?.typeOfCare === "Self_Care" ||
              selectedDispositionKey === "discharged") ? (
              <div>
                {/* PROCEED TO DISPATCH BUTTON */}
                <div>
                  {isCheckboxSelected && (
                    <div className="flex justify-end mt-[40px]">
                      <Button
                        variant={"contained"}
                        onClickCb={() => {
                          handleDischargeClick();
                        }}
                        label="Discharge"
                        style={{
                          paddingTop: "6px",
                          paddingBottom: "6px",
                          minWidth: "155px",
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              comesFrom !== "viewVisitNote" && (
                <div>
                  {/* PROCEED TO DISPATCH BUTTON */}
                  <div>
                    {isCheckboxSelected && (
                      <div className="flex justify-end mt-[40px]">
                        <Button
                          variant={"contained"}
                          onClickCb={() => {
                            handleSubmitClick();
                          }}
                          label="Submit"
                          style={{
                            paddingTop: "6px",
                            paddingBottom: "6px",
                            minWidth: "155px",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )
            )}

            {signVisit && (
              <div>
                {/* PROCEED TO DISPATCH BUTTON */}
                <div>
                  {isCheckboxSelected && (
                    <div className="flex justify-end mt-[40px]">
                      <Button
                        variant={"contained"}
                        onClickCb={() => {
                          handleSubmitClick();
                        }}
                        label="Submit"
                        style={{
                          paddingTop: "6px",
                          paddingBottom: "6px",
                          minWidth: "155px",
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {visitNoteCareFlowData?.visit_note?.[index]?.isVisitSign &&
              visitNoteCareFlowData?.visit_note?.[index]?.signedBy?.id && (
                <div className="pt-3 pl-4 pb-4 pr-4 text-end flex justify-end important-content">
                  <div
                    style={{
                      display: "flex, border:'1px solid red",
                      background: "#F4F4F4",
                      padding: "10px 20px",
                    }}
                    className="text-center border-radius-8px"
                  >
                    <div style={{ fontSize: "32px", fontFamily: "Sacramento" }}>
                      {visitNoteCareFlowData?.visit_note?.[index]?.signedBy
                        ?.firstName +
                        " " +
                        visitNoteCareFlowData?.visit_note?.[index]?.signedBy
                          ?.lastName}
                    </div>
                    <div className="font-weight-500 font-size-12px color-565656 ">
                      Signature
                    </div>
                  </div>
                </div>
              )}
            {comesFrom === "viewVisitNote" &&
              visitNoteCareFlowData?.visit_note?.[index]?.isVisitSign && (
                <div className="no-print-button">
                  {renderSendOptions()}
                  <div className="flex justify-end pr-4 mt-2">
                    <Button
                      label="Send"
                      onClickCb={() => handleSendNote()}
                      variant="contained"
                      disabled={!isAnySendOptionSelected}
                      style={{ padding: "6px 24px", minWidth: "120px" }}
                    />
                  </div>
                </div>
              )}
            {visitNoteCareFlowData?.visit_note?.[index]?.isVisitSign && (
              <div className="flex justify-end pr-4 mt-[40px] no-print-button">
                <Button
                  label={TEXT_CONSTANTS?.PRINT}
                  onClickCb={() => reactToPrintFn()}
                  variant="outlined"
                  style={{ padding: "5px 10px" }}
                />
              </div>
            )}
          </div>
        </DialogContent>
        {/* <DialogActions></DialogActions> */}
      </BootstrapDialog>
    </React.Fragment>
  );
}

const VisitNoteSection = ({
  title,
  content,
}: {
  title: string;
  content: string;
}) => (
  <div className="pt-3 pl-4 pb-4 pr-4">
    <Heading title={title} customStyle={headerTitleFrontStyle} />
    <div className="font-weight-500 color-979797 font-size-12px">
      {content?.charAt(0)?.toUpperCase() + content?.substring(1)}
    </div>
  </div>
);
